"use client";

import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Wrench, CircleAlert as AlertTriangle, CircleCheck as CheckCircle2, PhoneCall, ShieldCheck, Droplets, RotateCw, SlidersHorizontal as Sliders, CircleQuestionMark as HelpCircle, Clock, ArrowRight } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
const productLines = [{
  id: 1,
  name: "Class Five Technology",
  description: "Features an extra-large 3.25-inch canister flush valve engineered for rapid water release and maximum bulk waste removal.",
  feature: "High-volume bulk flush",
  models: "Cimarron, Memoirs Stately",
  iconName: "bulk"
}, {
  id: 2,
  name: "AquaPiston Canister Flush",
  description: "Replaces traditional rubber flappers with a 360-degree cylindrical tower seal to prevent warping and persistent tank leaks.",
  feature: "360-degree water entry",
  models: "Santa Rosa, Highline Arc",
  iconName: "piston"
}, {
  id: 3,
  name: "Revolution 360 Swirl",
  description: "Uses a dynamic single swirl jet to rinse the entire bowl surface thoroughly with every flush cycle.",
  feature: "Complete bowl coverage",
  models: "Corbelle, Betts, Kingston",
  iconName: "swirl"
}, {
  id: 4,
  name: "ContinuousClean Systems",
  description: "Factory-integrated dispensing system inside the tank that evenly mixes cleaning solution with every flush cycle.",
  feature: "Auto-dispensing reservoir",
  models: "Betts Clean, Cimarron Touchless",
  iconName: "clean"
}];
const troubleshootingSteps = [{
  id: "item-1",
  issue: "Ghost Flushing or Constant Water Running",
  cause: "Worn yellow/red silicone AquaPiston canister seal gasket or debris on the flush valve seat.",
  solution: "Turn off water at the wall stop. Twist the AquaPiston center stem a quarter-turn counterclockwise, lift out the canister, inspect the bottom gasket ring (part #GP1059291), and replace the silicone ring.",
  urgency: "Moderate - Wastes water daily"
}, {
  id: "item-2",
  issue: "Weak or Incomplete Bowl Evacuation",
  cause: "Low tank water level, disconnected canister guide rod, or calcified rim feed jets.",
  solution: "Check that the water line sits exactly at the stamped waterline mark inside the tank. Adjust the threaded fill valve rod. Clear mineral buildup in the upper rim holes with a soft brush and diluted vinegar.",
  urgency: "Standard maintenance"
}, {
  id: "item-3",
  issue: "Slow Tank Refill or High-Pitched Hissing",
  cause: "Clogged inlet diaphragm inside the Kohler Silent Fill Valve (part #GP1083167) from sediment.",
  solution: "Shut off water, unscrew the top valve cap while holding the base, place a cup over the open valve, and briefly open the shutoff valve to flush line debris before resealing.",
  urgency: "High - Strain on valve mechanism"
}, {
  id: "item-4",
  issue: "Loose Flush Handle or Touchless Sensor Failure",
  cause: "Slack trip lever bracket cable, worn axle bushing, or dead AA battery pack in touchless modules.",
  solution: "Tighten the reverse-threaded brass nut inside the tank or replace depleted batteries in the waterproof module atop the canister.",
  urgency: "Low - Mechanical adjustment"
}];
const oemParts = [{
  id: 1,
  name: "AquaPiston Yellow Seal Gasket",
  partNumber: "GP1059291",
  purpose: "Stops phantom running water on Kohler canister toilets.",
  availability: "Stocked on all Jonesboro service trucks"
}, {
  id: 2,
  name: "Kohler Silent Fill Valve Kit",
  partNumber: "GP1083167",
  purpose: "Factory replacement for fast, quiet tank refills.",
  availability: "Immediate local replacement"
}, {
  id: 3,
  name: "Class Five Flapper Assembly (3 1/4\")",
  partNumber: "GP1078440",
  purpose: "Restores optimal flush velocity on older flapper models.",
  availability: "Standard inventory item"
}, {
  id: 4,
  name: "Complete Tank-to-Bowl Gasket & Hardware",
  partNumber: "GP51487",
  purpose: "Eliminates leaks between the ceramic tank and bowl base.",
  availability: "Same-day service available"
}];
export default function KohlerGuide() {
  const [activeTab, setActiveTab] = useState<"diagnostics" | "systems" | "parts" | "warranty">("diagnostics");
  const shouldReduceMotion = useReducedMotion();
  const fadeIn = shouldReduceMotion ? {} : {
    initial: {
      opacity: 0,
      y: 16
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    viewport: {
      once: true
    },
    transition: {
      duration: 0.45,
      ease: "easeOut"
    }
  };
  return <section id="kohlertoiletservicesandtroubleshooting" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header Block */}
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <Badge variant="secondary" className="mb-3 px-3 py-1 text-xs font-semibold uppercase tracking-wider">
            Brand-Specific Plumbing Guidance
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold text-foreground tracking-tight mb-4">
            Kohler Toilet Diagnostics & Repair Support
          </h2>
          <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
            Professional repair procedures, tank component maintenance, and OEM parts replacement for Kohler Class Five, AquaPiston, and Revolution 360 models across Jonesboro, GA.
          </p>
        </div>

        {/* Emergency Alert Banner */}
        <motion.div {...fadeIn} className="mb-10">
          <Alert className="border-accent/40 bg-card shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4 p-5">
            <div className="flex items-start gap-3">
              <AlertTriangle className="size-5 text-accent shrink-0 mt-0.5" />
              <div>
                <AlertTitle className="text-foreground font-semibold text-base">
                  Experiencing an overflowing or actively leaking Kohler toilet?
                </AlertTitle>
                <AlertDescription className="text-muted-foreground text-sm mt-1">
                  Our licensed Jonesboro plumbers provide 24/7 rapid emergency dispatch equipped with genuine Kohler factory seals and valves.
                </AlertDescription>
              </div>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <a href="tel:+14708479076" className="w-full sm:w-auto">
                <Button variant="default" size="default" className="w-full gap-2 shadow-sm font-semibold">
                  <PhoneCall className="size-4" />
                  Call 24/7 Dispatch
                </Button>
              </a>
            </div>
          </Alert>
        </motion.div>

        {/* Navigation Selector Buttons */}
        <div className="flex flex-wrap items-center justify-center gap-2 p-1.5 bg-muted rounded-xl mb-10 max-w-2xl mx-auto">
          <Button variant={activeTab === "diagnostics" ? "default" : "ghost"} size="sm" onClick={() => setActiveTab("diagnostics")} className="flex-1 min-w-[130px] text-xs sm:text-sm font-medium">
            <Wrench className="size-4 mr-1.5" />
            Troubleshooting
          </Button>
          <Button variant={activeTab === "systems" ? "default" : "ghost"} size="sm" onClick={() => setActiveTab("systems")} className="flex-1 min-w-[130px] text-xs sm:text-sm font-medium">
            <Sliders className="size-4 mr-1.5" />
            Product Lines
          </Button>
          <Button variant={activeTab === "parts" ? "default" : "ghost"} size="sm" onClick={() => setActiveTab("parts")} className="flex-1 min-w-[130px] text-xs sm:text-sm font-medium">
            <RotateCw className="size-4 mr-1.5" />
            OEM Parts
          </Button>
          <Button variant={activeTab === "warranty" ? "default" : "ghost"} size="sm" onClick={() => setActiveTab("warranty")} className="flex-1 min-w-[130px] text-xs sm:text-sm font-medium">
            <ShieldCheck className="size-4 mr-1.5" />
            Warranty Support
          </Button>
        </div>

        {/* Tab 1: Diagnostic Matrix & Accordion */}
        {activeTab === "diagnostics" && <motion.div {...fadeIn} className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            <div className="lg:col-span-7 space-y-4">
              <div className="mb-4">
                <h3 className="text-xl font-bold text-foreground mb-1">
                  Step-by-Step Problem Resolution
                </h3>
                <p className="text-sm text-muted-foreground">
                  Select your Kohler toilet issue below for cause analysis and factory-recommended repair instructions.
                </p>
              </div>

              <Accordion type="single" collapsible defaultValue="item-1" className="w-full space-y-3">
                {troubleshootingSteps.map((step, i) => <AccordionItem key={step.id} value={step.id} data-index={i} className="border border-border rounded-lg px-4 bg-card shadow-xs">
                    <AccordionTrigger className="hover:no-underline py-4 text-left">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pr-2 w-full">
                        <span className="font-semibold text-foreground text-sm sm:text-base">
                          {step.issue}
                        </span>
                        <Badge variant="outline" className="text-[11px] w-fit font-normal text-muted-foreground">
                          {step.urgency}
                        </Badge>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pt-2 pb-4 text-muted-foreground">
                      <div className="space-y-3">
                        <div>
                          <span className="text-xs font-semibold text-foreground uppercase tracking-wide block mb-1">
                            Primary Cause:
                          </span>
                          <p className="text-sm text-card-foreground bg-muted/60 p-2.5 rounded-md">
                            {step.cause}
                          </p>
                        </div>
                        <div>
                          <span className="text-xs font-semibold text-foreground uppercase tracking-wide block mb-1">
                            Repair Procedure:
                          </span>
                          <p className="text-sm leading-relaxed">
                            {step.solution}
                          </p>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>)}
              </Accordion>
            </div>

            <div className="lg:col-span-5 space-y-6">
              <Card className="border-border shadow-md">
                <CardHeader>
                  <div className="flex items-center gap-2 text-primary mb-1">
                    <Droplets className="size-5 text-primary" />
                    <span className="text-xs font-bold uppercase tracking-wider">Quick Identification</span>
                  </div>
                  <CardTitle className="text-lg">Locating Your Kohler Model Number</CardTitle>
                  <CardDescription>
                    Essential for ordering exact internal gaskets and replacement valves.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4 text-sm text-muted-foreground">
                  <p>
                    Carefully lift off the ceramic tank lid and inspect the inner back wall of the water tank. Kohler stamps a 4-digit or 5-digit number starting with <strong>K-</strong> (such as <em>K-4634</em> for Cimarron tanks).
                  </p>
                  <ul className="space-y-2 text-foreground">
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="size-4 text-accent shrink-0" />
                      <span>Look for stamped ink near the waterline</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="size-4 text-accent shrink-0" />
                      <span>Note whether your flush tower is red, yellow, or black</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="size-4 text-accent shrink-0" />
                      <span>Check the date code molded into the porcelain</span>
                    </li>
                  </ul>
                </CardContent>
                <CardFooter className="bg-muted/40 rounded-b-xl border-t border-border pt-4 flex flex-col sm:flex-row gap-3">
                  <Link to="/toilet-repair/" className="w-full">
                    <Button variant="outline" size="sm" className="w-full font-medium">
                      General Toilet Repair Guide
                    </Button>
                  </Link>
                </CardFooter>
              </Card>

              <div className="p-4 rounded-xl bg-card border border-border shadow-xs flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Clock className="size-5 text-primary" />
                  <div>
                    <h4 className="text-sm font-semibold text-foreground">Need Urgent Kohler Service?</h4>
                    <p className="text-xs text-muted-foreground">Local Jonesboro technicians on call 24 hours.</p>
                  </div>
                </div>
                <Link to="/emergency-plumbing/">
                  <Button variant="default" size="sm">
                    Schedule
                  </Button>
                </Link>
              </div>
            </div>
          </motion.div>}

        {/* Tab 2: Product Lines */}
        {activeTab === "systems" && <motion.div {...fadeIn} className="space-y-6">
            <div className="text-left max-w-2xl mb-6">
              <h3 className="text-xl font-bold text-foreground">Kohler Flush Technology & Models</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Kohler utilizes specialized tank valves that differ substantially from generic hardware store parts.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {productLines.map((line, i) => <Card key={line.id} data-index={i} className="border-border shadow-sm flex flex-col justify-between">
                  <CardHeader className="pb-3">
                    <div className="size-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center mb-3">
                      <RotateCw className="size-5" />
                    </div>
                    <CardTitle className="text-base font-bold">{line.name}</CardTitle>
                    <Badge variant="secondary" className="w-fit text-[11px] font-medium mt-1">
                      {line.feature}
                    </Badge>
                  </CardHeader>
                  <CardContent className="space-y-3 text-sm text-muted-foreground flex-1">
                    <p>{line.description}</p>
                    <div className="pt-2 border-t border-border/60">
                      <span className="text-xs font-semibold text-foreground block mb-0.5">Common Series:</span>
                      <span className="text-xs text-foreground/80">{line.models}</span>
                    </div>
                  </CardContent>
                  <CardFooter className="pt-2">
                    <Link to="/toilet-problems/" className="w-full">
                      <Button variant="ghost" size="sm" className="w-full justify-between text-xs px-2 hover:bg-muted">
                        <span>View Known Issues</span>
                        <ArrowRight className="size-3.5" />
                      </Button>
                    </Link>
                  </CardFooter>
                </Card>)}
            </div>
          </motion.div>}

        {/* Tab 3: OEM Parts vs Universal */}
        {activeTab === "parts" && <motion.div {...fadeIn} className="space-y-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-4">
                <div className="mb-2">
                  <h3 className="text-xl font-bold text-foreground">Essential Kohler Factory Replacement Parts</h3>
                  <p className="text-sm text-muted-foreground">
                    Using universal 2-inch flappers on Kohler 3-inch or AquaPiston toilets causes improper seal tension and high water bills.
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {oemParts.map((part, i) => <div key={part.id} data-index={i} className="p-4 rounded-xl border border-border bg-card shadow-xs flex flex-col justify-between">
                      <div>
                        <div className="flex items-center justify-between gap-2 mb-1.5">
                          <span className="text-xs font-mono font-bold bg-muted px-2 py-0.5 rounded text-foreground">
                            {part.partNumber}
                          </span>
                          <span className="text-[11px] text-accent-foreground bg-accent/20 px-2 py-0.5 rounded font-medium">
                            OEM Direct
                          </span>
                        </div>
                        <h4 className="text-sm font-bold text-card-foreground mb-1">{part.name}</h4>
                        <p className="text-xs text-muted-foreground mb-3">{part.purpose}</p>
                      </div>
                      <div className="text-[11px] font-medium text-foreground/90 flex items-center gap-1.5 pt-2 border-t border-border">
                        <CheckCircle2 className="size-3.5 text-accent shrink-0" />
                        <span>{part.availability}</span>
                      </div>
                    </div>)}
                </div>
              </div>

              <div className="bg-card border border-border rounded-xl p-6 shadow-sm flex flex-col justify-between">
                <div>
                  <Badge variant="outline" className="mb-3">Plumber's Warning</Badge>
                  <h4 className="text-base font-bold text-foreground mb-2">Why Universal Flappers Fail on Kohler</h4>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                    Kohler canister towers use engineered hydrodynamic drop speeds. Off-the-shelf universal flappers cause short-flushing, handle drag, or constant trickle into the overflow pipe.
                  </p>
                  <div className="p-3 bg-muted rounded-lg text-xs space-y-1.5 text-foreground">
                    <p className="font-semibold">Our Guarantee:</p>
                    <p className="text-muted-foreground">We install 100% genuine Kohler replacement seals, valves, and trip levers for lasting factory performance.</p>
                  </div>
                </div>
                <div className="pt-6">
                  <Link to="/contact/" className="w-full">
                    <Button variant="default" className="w-full font-semibold">
                      Request OEM Part Replacement
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </motion.div>}

        {/* Tab 4: Warranty Support */}
        {activeTab === "warranty" && <motion.div {...fadeIn} className="max-w-4xl mx-auto space-y-6">
            <Card className="border-border shadow-md">
              <CardHeader>
                <div className="flex items-center gap-2 text-primary mb-1">
                  <ShieldCheck className="size-5 text-primary" />
                  <span className="text-xs font-bold uppercase tracking-wider">Manufacturer Coverage Details</span>
                </div>
                <CardTitle className="text-xl">Kohler Toilet Warranty & Repair Support</CardTitle>
                <CardDescription>
                  Understanding what is covered under Kohler's limited lifetime vitreous china warranty vs mechanical tank components.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6 text-sm">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 rounded-lg bg-muted/60 border border-border">
                    <h4 className="font-bold text-foreground mb-1">Vitreous China (Porcelain)</h4>
                    <p className="text-xs text-muted-foreground mb-2">Bowl, Tank Body, and Ceramic Lid</p>
                    <p className="text-sm text-card-foreground">
                      Covered under Kohler's Limited Lifetime or 1-to-5 Year warranties against manufacturing structural cracks and defects in the porcelain casting.
                    </p>
                  </div>
                  <div className="p-4 rounded-lg bg-muted/60 border border-border">
                    <h4 className="font-bold text-foreground mb-1">Internal Mechanical Components</h4>
                    <p className="text-xs text-muted-foreground mb-2">Fill Valves, Canisters, Levers & Seals</p>
                    <p className="text-sm text-card-foreground">
                      Typically warranted for 1 year from purchase. Chemical drop-in bowl cleaners placed inside the tank void internal gasket warranties.
                    </p>
                  </div>
                </div>

                <Separator />

                <div className="space-y-3">
                  <h4 className="font-bold text-foreground">How Jonesboro Emergency Plumber Assists with Warranty Claims:</h4>
                  <ul className="space-y-2 text-muted-foreground text-sm">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="size-4 text-accent shrink-0 mt-0.5" />
                      <span>We document factory model codes, serial identifiers, and defective part numbers for your warranty claim submission.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="size-4 text-accent shrink-0 mt-0.5" />
                      <span>We carry approved replacement parts on-truck so you don't have to wait weeks for manufacturer postal shipping.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="size-4 text-accent shrink-0 mt-0.5" />
                      <span>We guarantee our installation labor on every Kohler toilet overhaul and repair.</span>
                    </li>
                  </ul>
                </div>
              </CardContent>
              <CardFooter className="bg-muted/30 border-t border-border pt-4 flex flex-col sm:flex-row justify-between items-center gap-4">
                <span className="text-xs text-muted-foreground text-center sm:text-left">
                  Need a certified plumbing inspection for a Kohler warranty claim?
                </span>
                <Link to="/contact/">
                  <Button variant="default" size="sm" className="font-semibold">
                    Book Service Inspection
                  </Button>
                </Link>
              </CardFooter>
            </Card>
          </motion.div>}

        {/* Bottom Help / Cross-Navigation */}
        <div className="mt-16 pt-10 border-t border-border grid grid-cols-1 md:grid-cols-3 gap-6 text-center md:text-left">
          <div className="p-4 rounded-lg bg-card border border-border/80">
            <h4 className="font-bold text-foreground text-sm mb-1">Other Toilet Issues?</h4>
            <p className="text-xs text-muted-foreground mb-3">
              Explore diagnostics for non-Kohler fixtures, pressure-assist systems, and main sewer clogs.
            </p>
            <Link to="/toilet-problems/">
              <Button variant="link" size="sm" className="p-0 h-auto text-xs font-semibold">
                Explore All Toilet Problems &rarr;
              </Button>
            </Link>
          </div>

          <div className="p-4 rounded-lg bg-card border border-border/80">
            <h4 className="font-bold text-foreground text-sm mb-1">Water Line & Valve Leaks</h4>
            <p className="text-xs text-muted-foreground mb-3">
              Corroded quarter-turn supply valves or leaking braided steel hoses behind the bowl.
            </p>
            <Link to="/leak-detection-repair/">
              <Button variant="link" size="sm" className="p-0 h-auto text-xs font-semibold">
                Water Leak Detection Services &rarr;
              </Button>
            </Link>
          </div>

          <div className="p-4 rounded-lg bg-card border border-border/80">
            <h4 className="font-bold text-foreground text-sm mb-1">Major Fixture Upgrade</h4>
            <p className="text-xs text-muted-foreground mb-3">
              Replacing older inefficient toilets with high-performance WaterSense Kohler commodes.
            </p>
            <Link to="/services/">
              <Button variant="link" size="sm" className="p-0 h-auto text-xs font-semibold">
                View All Plumbing Services &rarr;
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>;
}